import type { NavigationItem } from '../ha-navigation-item/navigation-item.interface';

/**
 * Pure, directly testable: applies the feature-flag gate to a sidebar nav item list.
 *
 * Deliberately kept in its own module, separate from ha-app.component.ts: that component
 * pulls in the app's full DI/component graph at import time (same reason route-filters.ts is
 * split out of routes.ts — see that file's docblock), which drags in browser-only globals
 * that don't exist in vitest's node test environment without an escalating chain of shims.
 * This file has none of that, so it can be unit tested by importing it directly.
 */
export const buildNavItems = (
    myViewEnabled: boolean,
    modeNavItems: NavigationItem[]
): NavigationItem[] => {
    // Hide the unfinished "Meine Ansicht" (/by-me) nav entry when its build-time flag is
    // off. The nav data is injected from backend/views/index.html where the route path is
    // 'by-me' (no leading slash), so match on that exact value.
    return myViewEnabled
        ? modeNavItems
        : modeNavItems.filter(item => item.route?.path !== 'by-me');
};

/**
 * Whether a nav item counts as "active" for the current URL — pulled out of
 * ha-app.component.ts's render loop for the same testability reason as
 * `buildNavItems` above (Anke, 2026-09-06: My Organizer's new sidebar
 * submenu, "Meine Boxen" + "Mein Kalender", introduced the first REAL case
 * of two sibling nav items whose paths nest; since 2026-10-17 they are
 * `personal-organizer` ("Mein Kalender") and `personal-organizer/boxes`).
 *
 * A nav item is active on an exact path match, OR when `currentPath` is one
 * of its own child routes (`currentPath.startsWith(path + '/')` — e.g.
 * `/knowledge/<sid>/<slug>` staying under the `knowledge` entry). That
 * "child route" rule alone would ALSO mark "Mein Kalender" (`personal-
 * organizer`) active while viewing "Meine Boxen"
 * (`personal-organizer/boxes`) — `personal-organizer/boxes` textually
 * starts with `personal-organizer/` too, even though the two are unrelated
 * sibling PAGES, not a parent/detail pair. So a prefix match only counts
 * when no OTHER item in the same list matches `currentPath` more precisely
 * (exactly, or via a longer prefix) — that sibling's own exact/longer match
 * always wins.
 */
export const isNavItemActive = (
    navigationItem: NavigationItem,
    allNavItems: NavigationItem[],
    currentPath: string
): boolean => {
    const path = navigationItem.route?.path;
    if (!path) {
        return false;
    }
    if (path === currentPath) {
        return true;
    }
    if (!currentPath.startsWith(path + '/')) {
        return false;
    }
    const shadowedByMoreSpecificSibling = allNavItems.some(other => {
        const otherPath = other.route?.path;
        if (!otherPath || otherPath === path) {
            return false;
        }
        return otherPath === currentPath
            || (currentPath.startsWith(otherPath + '/') && otherPath.length > path.length);
    });
    return !shadowedByMoreSpecificSibling;
};

/**
 * "My Organizer" mode's sidebar submenu, in display order — kept here, as plain data, so the
 * order is unit tested (ha-app.component.ts only maps it to nav items). Anke, 2026-10-17:
 * "bitte die menupunkte 'Mein Kalender' und 'Meine Boxen' tauschen. es soll zuerst 'Mein
 * Kalender' ausgewählt sein" — the first entry owns the mode's root path, which the mode switch
 * opens.
 */
export const PERSONAL_ORGANIZER_NAV: readonly { readonly labelKey: string; readonly path: string }[] = [
    { labelKey: 'personal-organizer.nav.calendar', path: 'personal-organizer' },
    { labelKey: 'personal-organizer.nav.boxes', path: 'personal-organizer/boxes' },
];
